import React, { useMemo, useState } from "react";
import { Sparkles, MapPin, Star, Clock, ShieldCheck, PoundSterling, ArrowRight, Bot, Send, RefreshCw } from "lucide-react";
import { Pill, Avatar, Mono, Btn, Panel, Bar } from "./primitives";

const SUPPLIERS = [
  { id: "NH", name: "North Heat Ltd", trade: "Gas · HVAC", dist: 3.2, rating: 4.9, sla: 96, price: 0.92, comp: true, avail: "Free in 45m" },
  { id: "QM", name: "Quayside Mechanical", trade: "Mechanical", dist: 5.8, rating: 4.6, sla: 91, price: 0.88, comp: true, avail: "Today 16:00" },
  { id: "CP", name: "Capital Plumbing", trade: "Plumbing · Gas", dist: 7.4, rating: 4.4, sla: 88, price: 0.81, comp: true, avail: "Tomorrow" },
  { id: "GG", name: "Greenline Gas", trade: "Gas Safe", dist: 2.1, rating: 4.2, sla: 79, price: 0.95, comp: false, avail: "Free now" },
];

export const DEFAULT_WEIGHTS = { dist: 30, rating: 25, sla: 20, price: 25 };

export function scoreSupplier(s, w) {
  const total = w.dist + w.rating + w.sla + w.price || 1;
  const distScore = Math.max(0, 1 - s.dist / 12);
  const ratingScore = (s.rating - 3.5) / 1.5;
  const slaScore = s.sla / 100;
  const priceScore = s.price;
  const raw = (distScore * w.dist + ratingScore * w.rating + slaScore * w.sla + priceScore * w.price) / total;
  return Math.round(raw * 100);
}

export const AIMatch = ({ interactive = false, weights: ext, onWeights }) => {
  const [local, setLocal] = useState(DEFAULT_WEIGHTS);
  const weights = ext || local;
  const setW = (k, v) => { const n = { ...weights, [k]: Number(v) }; onWeights ? onWeights(n) : setLocal(n); };
  const ranked = useMemo(() => SUPPLIERS.map((s) => ({ ...s, score: s.comp ? scoreSupplier(s, weights) : null })).sort((a, b) => (b.score ?? -1) - (a.score ?? -1)), [weights]);
  const top = ranked[0];

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-mk-lime"><Sparkles className="h-3 w-3" />AI Supplier Matching · JOB-91AD22C0</div>
          <div className="font-display text-base font-bold mt-0.5">Leak investigation · 14 Mare St, E8</div>
          <div className="mt-1 flex flex-wrap gap-1"><Pill tone="dim">Plumbing · Gas</Pill><Pill tone="red">High priority</Pill><Pill tone="dim" mono>SLA 4h</Pill><Pill tone="amber">Vulnerable resident</Pill></div>
        </div>
        <Pill tone="lime" mono>ranked in 0.4s</Pill>
      </div>

      {interactive && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 rounded-lg border border-mk-border bg-mk-bg/60 p-2.5">
          {[["dist", "Distance", MapPin], ["rating", "Rating", Star], ["sla", "SLA hit-rate", Clock], ["price", "Price history", PoundSterling]].map(([k, l, I]) => (
            <label key={k} className="text-[10px]">
              <div className="flex items-center justify-between text-mk-dim mb-1"><span className="flex items-center gap-1"><I className="h-3 w-3" />{l}</span><Mono>{weights[k]}%</Mono></div>
              <input type="range" min="0" max="60" value={weights[k]} onChange={(e) => setW(k, e.target.value)} className="range-lime w-full" style={{ "--pct": `${(weights[k] / 60) * 100}%` }} data-testid={`ai-demo-weight-${k}`} />
            </label>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-mk-border overflow-hidden text-[11px] min-w-[560px]">
        <div className="grid grid-cols-[1.5fr_0.6fr_0.6fr_0.6fr_0.8fr_0.8fr] gap-2 border-b border-mk-border bg-mk-bg/60 px-3 py-1.5 text-[10px] uppercase tracking-wider text-mk-muted"><span>Supplier</span><span>Dist</span><span>Rating</span><span>SLA</span><span>Compliance</span><span className="text-right">Score</span></div>
        {ranked.map((s, i) => (
          <div key={s.id} className={`grid grid-cols-[1.5fr_0.6fr_0.6fr_0.6fr_0.8fr_0.8fr] gap-2 items-center px-3 py-2 border-b border-mk-border/60 last:border-0 ${i === 0 && s.comp ? "bg-mk-lime/[0.06]" : ""} ${!s.comp ? "opacity-60" : ""}`} data-testid={`ai-demo-row-${s.id}`}>
            <div className="flex items-center gap-2 min-w-0"><Avatar initials={s.id} tone={i === 0 && s.comp ? "lime" : "blue"} /><div className="min-w-0"><div className="font-medium truncate flex items-center gap-1.5">{s.name}{i === 0 && s.comp && <Pill tone="lime">AI pick</Pill>}</div><div className="text-[10px] text-mk-muted truncate">{s.trade} · {s.avail}</div></div></div>
            <Mono className="text-mk-text">{s.dist} mi</Mono>
            <span className="flex items-center gap-1"><Star className="h-3 w-3 text-mk-lime fill-mk-lime" />{s.rating}</span>
            <Mono className="text-mk-text">{s.sla}%</Mono>
            <span>{s.comp ? <Pill tone="green"><ShieldCheck className="h-3 w-3" />Valid</Pill> : <Pill tone="red">Insurance lapsed</Pill>}</span>
            <div className="text-right">{s.comp ? <><div className="font-display font-bold text-sm">{s.score}<span className="text-mk-muted text-[10px]">/100</span></div><Bar pct={s.score} tone={i === 0 ? "bg-mk-lime" : "bg-mk-blue"} className="mt-1" /></> : <span className="text-[10px] text-red-300">Blocked</span>}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-mk-lime/30 bg-mk-lime/5 p-3 text-[11px]">
        <div className="text-[10px] uppercase tracking-wider text-mk-lime mb-1">Why this supplier?</div>
        <p className="text-mk-dim leading-relaxed" data-testid="ai-demo-reasoning">
          <b className="text-mk-text">{top.name}</b> is {top.dist} mi away with a {top.rating}★ 90-day rating and {top.sla}% SLA hit-rate; quotes average {Math.round((1 - top.price) * 100)}% under panel median and all compliance documents are valid. Greenline Gas is closer but hard-blocked: public liability insurance expired.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Btn data-testid="ai-demo-assign-btn">Assign {top.name.split(" ")[0]} {top.name.split(" ")[1]}<ArrowRight className="h-3 w-3" /></Btn>
          <Btn tone="ghost">Manual override</Btn>
          <span className="text-[10px] text-mk-muted ml-auto">Decision logged · accept/reject trains your tenant's weights</span>
        </div>
      </div>
    </div>
  );
};

export const Reassign = () => (
  <Panel title="Suggested reassignments" right="Dashboard · today">
    <div className="space-y-2 text-[11px]">
      {[["Capital Plumbing", 11, "North Heat Ltd", 2, "JOB-4E9DC928 · Booster pump"], ["Sparks Electrical", 8, "Volt & Co", 1, "JOB-8BC1754F · Fire alarm panel"]].map(([from, fl, to, tl, job]) => (
        <div key={job} className="rounded-lg border border-mk-border p-2.5">
          <div className="flex items-center gap-2"><span className="font-medium">{from}</span><Pill tone="red">{fl} open</Pill><ArrowRight className="h-3 w-3 text-mk-lime" /><span className="font-medium">{to}</span><Pill tone="green">{tl} open</Pill><span className="ml-auto text-[10px] text-mk-lime flex items-center gap-1"><RefreshCw className="h-3 w-3" />Reassign?</span></div>
          <div className="mt-1 text-[10px] text-mk-muted"><Mono>{job}</Mono> · same trade, 1.4 mi closer, SLA at risk in 2h 10m</div>
        </div>
      ))}
    </div>
  </Panel>
);

export const AIDraft = () => (
  <Panel title={<span className="flex items-center gap-1.5"><Bot className="h-3.5 w-3.5 text-mk-lime" />AI Assistant · grounded in your live tenant data</span>} right="Your data never trains external models">
    <div className="space-y-2 text-[11px]">
      <div className="ml-auto max-w-[85%] rounded-xl rounded-tr-sm bg-mk-blue px-3 py-2 text-white">Draft a scope of works for the leak under the kitchen sink at 14 Mare St, Flat 7B — include caveats.</div>
      <div className="max-w-[92%] rounded-xl rounded-tl-sm bg-mk-raised px-3 py-2.5 text-mk-dim leading-relaxed">
        <div className="text-mk-text font-semibold mb-1">Scope of Works — Leak investigation, Flat 7B</div>
        <ol className="list-decimal pl-4 space-y-0.5">
          <li>Isolate supply; inspect trap, waste and flexi-hoses under sink.</li>
          <li>Pressure-test cold feed; replace failed compression joint or flexi as found.</li>
          <li>Moisture-check base unit and adjoining floor; report readings &gt; 18%.</li>
          <li>Reinstate, test, photograph before/after.</li>
        </ol>
        <div className="mt-1.5 text-[10px]"><b className="text-mk-text">Caveats:</b> excludes making good to kitchen units; asbestos survey (2024) shows none in this area. Resident flagged vulnerable — schedule with 24h notice.</div>
        <div className="mt-1.5 text-[10px] text-mk-muted">Est. £180–£260 · 1.5h · Suggested: North Heat Ltd (96/100)</div>
      </div>
      <div className="flex gap-1.5 flex-wrap">{["Add to quote Q-2046", "Compare 3 quotes", "Draft resident notice"].map((c) => <Pill key={c} tone="dim" className="!py-1 !px-2 cursor-pointer">{c}</Pill>)}</div>
      <div className="flex items-center gap-2 rounded-lg border border-mk-lime/40 bg-mk-bg px-3 py-2 text-mk-muted">Ask me anything…<Send className="ml-auto h-3.5 w-3.5 text-mk-lime" /></div>
    </div>
  </Panel>
);

export { SUPPLIERS };
